import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, ShieldAlert } from "lucide-react";

/**
 * Protected legal page wrapper.
 * Layers applied (best-effort; nothing is 100% on the open web):
 *   - CSS: user-select: none, no-print, no-touch-callout
 *   - JS: blocks copy / cut / contextmenu / drag / common dev-tool shortcuts
 *   - Meta: noindex, nofollow, noai, noimageai, noai-training (ethical AI signal)
 *   - DevTools blur detection (visual deterrent)
 */
export default function ProtectedLegalPage({ title, lastUpdated, children }) {
  useEffect(() => {
    // Inject anti-AI / no-index meta tags into <head> for this page.
    const metas = [
      ["robots",     "noindex, nofollow, noarchive, nosnippet, noimageindex"],
      ["googlebot",  "noindex, nofollow, noarchive, nosnippet"],
      ["bingbot",    "noindex, nofollow"],
      // Ethical AI crawler opt-out (respected by GPTBot, Google-Extended, ClaudeBot, etc.)
      ["robots",     "noai, noimageai, noai-training, SPC"],
    ];
    const created = metas.map(([name, content]) => {
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      m.setAttribute("content", content);
      document.head.appendChild(m);
      return m;
    });

    const block = (e) => { e.preventDefault(); e.stopPropagation(); return false; };

    const onKey = (e) => {
      // Block: Ctrl+C, Ctrl+X, Ctrl+A, Ctrl+S, Ctrl+P, Ctrl+U, F12, Ctrl+Shift+I/J/C
      const k = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;
      if (
        (ctrl && ["c", "x", "a", "s", "p", "u"].includes(k)) ||
        (e.key === "F12") ||
        (ctrl && shift && ["i", "j", "c"].includes(k))
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("copy",         block);
    document.addEventListener("cut",          block);
    document.addEventListener("contextmenu",  block);
    document.addEventListener("dragstart",    block);
    document.addEventListener("selectstart",  block);
    document.addEventListener("keydown",      onKey);

    return () => {
      document.removeEventListener("copy",        block);
      document.removeEventListener("cut",         block);
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("dragstart",   block);
      document.removeEventListener("selectstart", block);
      document.removeEventListener("keydown",     onKey);
      created.forEach((m) => m.remove());
    };
  }, []);

  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  return (
    <section
      className="max-w-4xl mx-auto px-6 lg:px-8 py-20 legal-protected"
      data-testid={`legal-page-${slug}`}
    >
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-10 selectable">
        <ArrowLeft className="w-4 h-4" /> Back home
      </Link>

      <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-300 border border-white/15 px-3 py-1.5 bg-white/5">
        <Sparkles className="w-3 h-3 text-[#F4A22A]" /> Legal · Techno Sort
      </div>

      <h1 className="font-display font-bold text-4xl md:text-6xl text-white mt-6 tracking-tight">
        {title}
      </h1>

      <div className="text-xs text-neutral-500 mt-3 font-mono">Last updated: {lastUpdated}</div>

      <div className="mt-6 inline-flex items-start gap-3 text-[11px] text-neutral-500 bg-white/[0.02] border border-white/10 px-4 py-3 max-w-2xl">
        <ShieldAlert className="w-3.5 h-3.5 text-[#1E5BA8] mt-0.5 shrink-0" />
        <span>
          This document is the intellectual property of Techno Sort. Copying, redistribution, indexing by search engines,
          and AI-training reuse are not permitted. All communications by email: technosort.rd@gmail.com
        </span>
      </div>

      <article className="prose-legal mt-12 text-neutral-300 leading-relaxed max-w-none">
        {children}
      </article>

      <div className="mt-16 text-[10px] uppercase tracking-[0.25em] text-neutral-600 font-mono">
        © 2026 Techno Sort · All rights reserved · Document ID: {slug}-{lastUpdated.replace(/\s/g, "")}
      </div>
    </section>
  );
}
